// API utility for handling environment-aware endpoints
// This ensures API calls work in both development (proxy to localhost:5000) and production (Firebase Functions)

import { auth } from '../firebase';
import { getCSRFToken } from './csrfUtils';

/**
 * Makes a fetch request to the API
 * In development, the proxy in package.json forwards /api/* to localhost:5000
 * In production, Firebase hosting rewrites /api/* to Firebase Functions
 * 
 * @param {string} endpoint - The API endpoint (e.g., '/api/checkUserStatus')
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export const apiCall = async (endpoint, options = {}) => {
  // Ensure endpoint starts with /api
  const apiEndpoint = endpoint.startsWith('/api') ? endpoint : `/api/${endpoint}`;
  
  // Default headers
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Get the current user's auth token if available
  let token = null;
  let userEmail = null;
  
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
      userEmail = auth.currentUser.email;
      defaultHeaders['Authorization'] = `Bearer ${token}`;
      
    } catch (tokenError) {
    }
  } else {
  }

  // Merge options
  const fetchOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  // Add CSRF token for state-changing operations
  const method = fetchOptions.method || 'GET';
  const csrfExemptEndpoints = [
    '/api/checkUserStatus',
    '/api/validateTutorEmail',
    '/api/getTutorsPublic',
    '/api/getTutorsBySubjects',
    '/api/testTutorEmails',
    '/api/csrf-token',
    '/api/public-csrf-token',
    '/api/oauth',
    '/api/googleAuth',
    '/api/google-signin',
    '/api/completeVerification',
    '/api/completeProfile',
    '/api/deletePendingUser',
    '/api/csp'
  ];
  
  const isExempt = csrfExemptEndpoints.some(endpoint => apiEndpoint.includes(endpoint));
  const needsCSRF = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase()) && !isExempt;
  
  if (needsCSRF && auth.currentUser) {
    try {
      const csrfToken = await getCSRFToken();
      fetchOptions.headers['X-CSRF-Token'] = csrfToken;
    } catch (error) {
      // Continue without CSRF token to avoid breaking functionality
    }
  }


  // Make the request
  const response = await fetch(apiEndpoint, fetchOptions);
  
  
  return response;
};

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: (endpoint, options = {}) => 
    apiCall(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, body, options = {}) => 
    apiCall(endpoint, { 
      ...options, 
      method: 'POST', 
      body: JSON.stringify(body) 
    }),
  
  put: (endpoint, body, options = {}) => 
    apiCall(endpoint, { 
      ...options, 
      method: 'PUT', 
      body: JSON.stringify(body) 
    }),
  
  delete: (endpoint, options = {}) => 
    apiCall(endpoint, { ...options, method: 'DELETE' }),
};

export default api;